(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const enhancedShadowRoots = new WeakSet();
  const reviewBoundForms = new WeakSet();
  const reviewState = new WeakMap();

  const productLabels = {
    humidifier: 'Humidifier',
    showerhead: 'Filtered Shower Head'
  };

  const helpCopy = {
    productModel: {
      lead: 'Where to find it:',
      text: ' Check the label on the bottom or back of your unit, or refer to the user manual.'
    },
    lotNumber: {
      lead: 'Where to find it:',
      text: ' Check the label on the bottom or back of your unit for “Lot No.”'
    },
    orderNumber: {
      text: 'Enter a valid Amazon order number (e.g., 111-1111111-1111111). If you received the product as a gift, enter “Gift”.'
    }
  };

  function normalizeFormId(value) {
    return String(value || '').replace(/^forms-root-/, '').trim();
  }

  function getBlockFormId(block) {
    const formRoot = block.querySelector('[data-forms-id]');
    return normalizeFormId(formRoot && formRoot.dataset.formsId);
  }

  function getShadowElementById(shadowRoot, id) {
    if (!shadowRoot) return null;
    if (typeof shadowRoot.getElementById === 'function') {
      return shadowRoot.getElementById(id);
    }
    return shadowRoot.querySelector(`[id="${id.replace(/"/g, '\\"')}"]`);
  }

  function normalizeFieldLabel(value) {
    return String(value || '')
      .replace(/\s+/g, ' ')
      .replace(/\s*[*:]\s*$/, '')
      .trim()
      .toLowerCase();
  }

  function replaceLegacyFormCopy(shadowRoot) {
    const replacements = [
      [/Register for Extended Warranty/gi, 'Register Your Product'],
      [/Extend My Warranty/gi, 'Register My Product'],
      [/Warranty Registration Received/gi, 'Product Registration Received'],
      [/Extended Warranty/gi, 'Product Registration'],
      [/Warranty Registration/gi, 'Product Registration']
    ];
    const walker = document.createTreeWalker(shadowRoot, NodeFilter.SHOW_TEXT);
    const textNodes = [];

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((node) => {
      const parent = node.parentElement;
      if (!parent || parent.matches('style, script')) return;

      let nextText = node.nodeValue || '';
      replacements.forEach(([pattern, replacement]) => {
        nextText = nextText.replace(pattern, replacement);
      });
      if (nextText !== node.nodeValue) node.nodeValue = nextText;
    });
  }

  function findFloatingLabel(shadowRoot, control) {
    if (!control) return null;

    if (control.id) {
      const directLabel = Array.from(shadowRoot.querySelectorAll('label'))
        .find((label) => label.htmlFor === control.id);
      if (directLabel) return directLabel;
    }

    const labelledBy = (control.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
    const ariaLabel = labelledBy
      .map((id) => getShadowElementById(shadowRoot, id))
      .find((item) => item && item !== control);
    if (ariaLabel) return ariaLabel;

    let container = control.parentElement;
    for (let depth = 0; container && container !== shadowRoot && depth < 3; depth += 1) {
      const nestedLabel = container.querySelector('label');
      if (nestedLabel) return nestedLabel;
      container = container.parentElement;
    }

    return null;
  }

  function updateFloatingLabelState(shadowRoot, control) {
    const label = findFloatingLabel(shadowRoot, control);
    if (!label) return;

    control.dataset.ccFloatingControl = 'true';
    label.dataset.ccFloatingLabel = 'true';

    let autofilled = false;
    try {
      autofilled = control.matches(':-webkit-autofill');
    } catch (_error) {
      autofilled = false;
    }

    const active = Boolean(
      String(control.value || '').trim()
      || shadowRoot.activeElement === control
      || control.matches(':focus')
      || autofilled
    );
    label.dataset.ccFloatingActive = active ? 'true' : 'false';
  }

  function syncFloatingLabels(shadowRoot) {
    const controls = shadowRoot.querySelectorAll(
      'input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="submit"]), textarea'
    );

    controls.forEach((control) => {
      if (control.dataset.ccFloatingBound !== 'true') {
        control.dataset.ccFloatingBound = 'true';
        ['input', 'change', 'focus', 'blur', 'animationstart'].forEach((eventName) => {
          control.addEventListener(eventName, () => updateFloatingLabelState(shadowRoot, control));
        });
      }
      updateFloatingLabelState(shadowRoot, control);
    });
  }

  function findFieldByLabel(shadowRoot, labelText) {
    const wanted = normalizeFieldLabel(labelText);
    const label = Array.from(shadowRoot.querySelectorAll('label, legend, [id$="-selector-label"]'))
      .find((item) => normalizeFieldLabel(item.textContent) === wanted);
    if (!label) return null;

    let control = null;
    const forId = label.getAttribute('for');
    if (forId) control = getShadowElementById(shadowRoot, forId);

    if (!control && label.id) {
      control = Array.from(shadowRoot.querySelectorAll('[aria-labelledby]')).find((item) => {
        const labelledBy = (item.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
        return labelledBy.includes(label.id);
      });
    }

    if (!control) {
      control = label.querySelector('input, textarea, select, button, [role="combobox"]')
        || label.parentElement?.querySelector('input, textarea, select, button, [role="combobox"]');
    }

    let container = label.parentElement;
    while (container && container !== shadowRoot && control && !container.contains(control)) {
      container = container.parentElement;
    }

    return {
      label,
      control,
      container: container && container !== shadowRoot ? container : (control?.parentElement || label.parentElement)
    };
  }

  function getFieldValueByLabel(shadowRoot, labelText) {
    const field = findFieldByLabel(shadowRoot, labelText);
    const control = field && field.control;
    if (!control) return '';

    if (control.matches('select')) {
      return String(control.options[control.selectedIndex]?.text || control.value || '').trim();
    }

    if (control.matches('input, textarea')) {
      return String(control.value || '').trim();
    }

    if (control.matches('[role="combobox"]')) {
      const labelledBy = (control.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
      const valueElement = labelledBy
        .filter((id) => id !== field.label.id)
        .map((id) => getShadowElementById(shadowRoot, id))
        .find((item) => item && normalizeFieldLabel(item.textContent) !== normalizeFieldLabel(labelText));

      if (valueElement) return String(valueElement.textContent || '').trim();

      const selectedValue = control.querySelector('[id$="-selector-value"], [data-selected-value]');
      if (selectedValue) return String(selectedValue.textContent || '').trim();
    }

    return String(control.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function addHelpTextByLabel(shadowRoot, labelText, key, copy) {
    const field = findFieldByLabel(shadowRoot, labelText);
    if (!field || !field.container) return;

    const { container, control } = field;

    const existing = Array.from(container.querySelectorAll('[data-cc-help-for]'))
      .find((item) => item.dataset.ccHelpFor === key);
    if (existing) return;

    const help = document.createElement('div');
    help.className = 'cc-inline-help';
    help.dataset.ccHelpFor = key;
    help.id = `cc-help-${key}`;

    if (copy.lead) {
      const strong = document.createElement('strong');
      strong.textContent = copy.lead;
      help.appendChild(strong);
    }

    help.appendChild(document.createTextNode(copy.text));
    container.appendChild(help);

    if (control) {
      const describedBy = new Set((control.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
      describedBy.add(help.id);
      control.setAttribute('aria-describedby', Array.from(describedBy).join(' '));
    }
  }

  function installFormHelp(shadowRoot) {
    replaceLegacyFormCopy(shadowRoot);

    if (!shadowRoot.querySelector('style[data-cc-form-enhancements]')) {
      const style = document.createElement('style');
      style.dataset.ccFormEnhancements = 'true';
      style.textContent = `
        /*
         * Shopify Forms uses floating labels inside its shadow root. The
         * default vertical spacing can become too tight with the storefront
         * typography, causing filled values to overlap their labels. Keep the
         * empty-field layout intact, then create more separation only when a
         * field is focused or filled by adjusting Shopify's own variables.
         */
        :host {
          --form-field-padding-top: max(calc(var(--form-field-padding, 15px) * 1.7), 26px);
          --form-field-padding-bottom: max(calc(var(--form-field-padding, 15px) * 0.55), 8px);
          --input-label-Y-transform: calc(var(--input-label-top, 16px) * -0.72);
          --input-label-transform: translateY(var(--input-label-Y-transform)) scale(0.8);
        }

        input[data-cc-floating-control="true"],
        textarea[data-cc-floating-control="true"] {
          padding-top: 22px !important;
          padding-bottom: 6px !important;
        }

        [data-cc-floating-label="true"] {
          transform-origin: top left !important;
          transition: top .15s ease, transform .15s ease, font-size .15s ease !important;
        }

        [data-cc-floating-label="true"][data-cc-floating-active="true"] {
          top: 7px !important;
          transform: none !important;
          font-size: 11px !important;
          line-height: 1 !important;
          pointer-events: none;
        }

        .cc-inline-help {
          margin: 6px 0 0;
          color: #6c6c6c;
          font-family: Arial, Helvetica, sans-serif;
          font-size: 12px;
          font-weight: 400;
          line-height: 1.45;
          text-align: left;
        }
        .cc-inline-help strong {
          color: #55504e;
          font-weight: 600;
        }
      `;
      shadowRoot.appendChild(style);
    }

    syncFloatingLabels(shadowRoot);

    addHelpTextByLabel(shadowRoot, 'Product Model', 'product-model', helpCopy.productModel);
    addHelpTextByLabel(shadowRoot, 'Lot Number', 'lot-number', helpCopy.lotNumber);
    addHelpTextByLabel(shadowRoot, 'Order Number', 'order-number', helpCopy.orderNumber);

  }

  function getInputValue(shadowRoot, id) {
    const input = getShadowElementById(shadowRoot, id);
    return input && 'value' in input ? String(input.value || '').trim() : '';
  }

  function getComboValue(shadowRoot, name) {
    const value = getShadowElementById(shadowRoot, `${name}-selector-value`);
    return value ? String(value.textContent || '').trim() : '';
  }

  function getPurchaseDate(shadowRoot) {
    const groups = Array.from(shadowRoot.querySelectorAll('[role="group"][aria-labelledby]'));
    const group = groups.find((item) => {
      const labelId = item.getAttribute('aria-labelledby');
      const label = labelId && getShadowElementById(shadowRoot, labelId);
      return Boolean(label && /purchase date/i.test(label.textContent || ''));
    });
    if (!group) return '';

    const inputs = Array.from(group.querySelectorAll('input'));
    const date = { day: '', month: '', year: '' };

    inputs.forEach((input) => {
      const value = String(input.value || '').trim();
      const hint = [input.placeholder, input.getAttribute('aria-label'), input.id]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      if (/(^|[^a-z])(dd|day)([^a-z]|$)/.test(hint)) date.day = value;
      else if (/(^|[^a-z])(mm|month)([^a-z]|$)/.test(hint)) date.month = value;
      else if (/(^|[^a-z])(yyyy|year)([^a-z]|$)/.test(hint)) date.year = value;
    });

    if (date.day || date.month || date.year) {
      return [date.day, date.month, date.year].filter(Boolean).join('/');
    }

    const parts = inputs.map((input) => String(input.value || '').trim());
    return parts.some(Boolean) ? parts.join('/') : '';
  }

  function requiredFieldsAreFilled(shadowRoot) {
    const requiredFields = shadowRoot.querySelectorAll(
      'input[aria-required="true"], textarea[aria-required="true"], button[role="combobox"][aria-required="true"]'
    );

    return Array.from(requiredFields).every((field) => {
      if (field.matches('button[role="combobox"]')) {
        const labelledBy = (field.getAttribute('aria-labelledby') || '').split(/\s+/).filter(Boolean);
        const valueId = labelledBy[labelledBy.length - 1];
        const valueElement = valueId && getShadowElementById(shadowRoot, valueId);
        return Boolean(valueElement && String(valueElement.textContent || '').trim());
      }

      if (field.type === 'checkbox' || field.type === 'radio') return field.checked;
      return Boolean(String(field.value || '').trim());
    });
  }

  function collectReviewFields(root, shadowRoot, product) {
    const categorySelect = root.querySelector('[data-registration-category]');

    return [
      ['First Name', getInputValue(shadowRoot, 'first_name') || getFieldValueByLabel(shadowRoot, 'First Name')],
      ['Last Name', getInputValue(shadowRoot, 'last_name') || getFieldValueByLabel(shadowRoot, 'Last Name')],
      ['Email Address', getInputValue(shadowRoot, 'email') || getFieldValueByLabel(shadowRoot, 'Email Address')],
      ['Country', getFieldValueByLabel(shadowRoot, 'Country') || getComboValue(shadowRoot, 'custom#country')],
      ['Purchase Platform', getFieldValueByLabel(shadowRoot, 'Purchase Platform') || getComboValue(shadowRoot, 'custom#purchase_platform')],
      ['Order Number', getFieldValueByLabel(shadowRoot, 'Order Number') || getInputValue(shadowRoot, 'custom#order_number')],
      ['Purchase Date', getPurchaseDate(shadowRoot)],
      ['Product Category', productLabels[product] || (categorySelect && categorySelect.options[categorySelect.selectedIndex]?.text) || ''],
      ['Product Model', getFieldValueByLabel(shadowRoot, 'Product Model') || getComboValue(shadowRoot, 'custom#product_model')],
      ['Lot Number', getFieldValueByLabel(shadowRoot, 'Lot Number') || getInputValue(shadowRoot, 'custom#lot_number')]
    ];
  }

  function closeReviewModal(root, restoreFocus = true) {
    const modal = root.querySelector('[data-registration-review-modal]');
    const state = reviewState.get(root);
    if (!modal) return;

    modal.hidden = true;
    document.documentElement.classList.remove('cc-review-open');

    if (restoreFocus && state && state.submitButton) {
      state.submitButton.focus({ preventScroll: true });
    }

    reviewState.delete(root);
  }

  function openReviewModal(root, shadowRoot, product, form) {
    const modal = root.querySelector('[data-registration-review-modal]');
    const list = modal && modal.querySelector('[data-review-list]');
    const dialog = modal && modal.querySelector('.cc-review-modal__dialog');
    const confirmButton = modal && modal.querySelector('[data-review-confirm]');
    const submitButton = shadowRoot.querySelector('button[type="submit"], [data-testid="btn-form-submit"]');
    if (!modal || !list || !dialog || !confirmButton || !submitButton) return;

    list.replaceChildren();
    collectReviewFields(root, shadowRoot, product).forEach(([label, value]) => {
      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      term.textContent = label;
      description.textContent = value || '—';
      row.append(term, description);
      list.appendChild(row);
    });

    confirmButton.disabled = false;
    confirmButton.textContent = 'Confirm & Submit';
    reviewState.set(root, { form, shadowRoot, submitButton });
    modal.hidden = false;
    document.documentElement.classList.add('cc-review-open');

    window.requestAnimationFrame(() => dialog.focus({ preventScroll: true }));
  }

  function initializeReviewModal(root) {
    const modal = root.querySelector('[data-registration-review-modal]');
    if (!modal || modal.dataset.reviewReady === 'true') return;
    modal.dataset.reviewReady = 'true';

    modal.querySelectorAll('[data-review-close]').forEach((button) => {
      button.addEventListener('click', () => closeReviewModal(root));
    });

    const editButton = modal.querySelector('[data-review-edit]');
    if (editButton) editButton.addEventListener('click', () => closeReviewModal(root));

    const confirmButton = modal.querySelector('[data-review-confirm]');
    if (confirmButton) {
      confirmButton.addEventListener('click', () => {
        const state = reviewState.get(root);
        if (!state || confirmButton.disabled) return;

        confirmButton.disabled = true;
        confirmButton.textContent = 'Submitting…';
        state.form.dataset.ccReviewBypass = 'true';
        modal.hidden = true;
        document.documentElement.classList.remove('cc-review-open');
        reviewState.delete(root);

        if (typeof state.form.requestSubmit === 'function') {
          state.form.requestSubmit(state.submitButton);
        } else {
          state.submitButton.click();
        }

        window.setTimeout(() => {
          confirmButton.disabled = false;
          confirmButton.textContent = 'Confirm & Submit';
        }, 1500);
      });
    }

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeReviewModal(root);
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(modal.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function bindReviewStep(root, shadowRoot, product) {
    const form = shadowRoot.querySelector('form[data-testid="form"], form');
    if (!form || reviewBoundForms.has(form)) return;
    reviewBoundForms.add(form);

    form.addEventListener('submit', (event) => {
      if (form.dataset.ccReviewBypass === 'true') {
        delete form.dataset.ccReviewBypass;
        return;
      }

      if (!requiredFieldsAreFilled(shadowRoot)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      openReviewModal(root, shadowRoot, product, form);
    }, true);
  }

  function enhanceShadowRoot(root, shadowRoot, product) {
    const applyEnhancements = () => {
      installFormHelp(shadowRoot);
      bindReviewStep(root, shadowRoot, product);
    };

    applyEnhancements();

    if (enhancedShadowRoots.has(shadowRoot)) return;
    enhancedShadowRoots.add(shadowRoot);

    [100, 500, 1500].forEach((delay) => {
      window.setTimeout(() => syncFloatingLabels(shadowRoot), delay);
    });

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        applyEnhancements();
      });
    });
    observer.observe(shadowRoot, { childList: true, subtree: true });
  }

  function enhanceAppBlock(root, block, product) {
    const host = block.querySelector('shopify-forms-embed');
    if (!host) return;

    if (host.shadowRoot) {
      enhanceShadowRoot(root, host.shadowRoot, product);
      return;
    }

    if (window.customElements && typeof window.customElements.whenDefined === 'function') {
      window.customElements.whenDefined('shopify-forms-embed').then(() => {
        if (host.shadowRoot) enhanceShadowRoot(root, host.shadowRoot, product);
      });
    }
  }

  function configureRegistrationForms(root) {
    const area = root.querySelector('[data-registration-app-area]');
    const category = root.querySelector('[data-registration-category]');
    const prompt = root.querySelector('[data-registration-form-prompt]');
    if (!area || !category) return;

    const expectedIds = {
      humidifier: normalizeFormId(area.dataset.humidifierFormId),
      showerhead: normalizeFormId(area.dataset.showerheadFormId)
    };
    const selected = category.value;
    const found = { humidifier: false, showerhead: false };

    area.querySelectorAll('[data-registration-app-block]').forEach((block) => {
      const formId = getBlockFormId(block);
      const product = Object.keys(expectedIds).find((key) => expectedIds[key] && expectedIds[key] === formId) || '';
      block.dataset.registrationProduct = product;
      if (product) found[product] = true;

      block.hidden = !selected || !product || product !== selected;
      if (!block.hidden) enhanceAppBlock(root, block, product);
    });

    area.dataset.registrationSwitchReady = 'true';

    if (prompt) {
      if (!selected) {
        prompt.textContent = 'Select a product category to begin your registration.';
        prompt.hidden = false;
      } else if (!found[selected]) {
        prompt.textContent = 'The selected registration form is temporarily unavailable. Please contact Clarston Support.';
        prompt.hidden = false;
      } else {
        prompt.hidden = true;
      }
    }
  }

  function initializeRegistrationForms(root) {
    const area = root.querySelector('[data-registration-app-area]');
    const category = root.querySelector('[data-registration-category]');
    if (!area || !category || area.dataset.registrationReady === 'true') return;
    area.dataset.registrationReady = 'true';

    initializeReviewModal(root);
    category.addEventListener('change', () => configureRegistrationForms(root));

    let scheduled = false;
    const observer = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        scheduled = false;
        configureRegistrationForms(root);
      });
    });
    observer.observe(area, { childList: true, subtree: true });

    configureRegistrationForms(root);
  }

  function initializeCustomerCare(root) {
    if (!root || root.dataset.clarstonCareReady === 'true') return;
    root.dataset.clarstonCareReady = 'true';

    root.querySelectorAll('.cc-faq-item').forEach((item) => {
      item.open = false;
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        const group = item.closest('.cc-faq-list');
        if (!group) return;

        group.querySelectorAll('.cc-faq-item[open]').forEach((openItem) => {
          if (openItem !== item) openItem.open = false;
        });
      });
    });

    root.querySelectorAll('a[href^="#clarston-"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = root.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
          block: 'start'
        });
        window.history.replaceState(null, '', link.getAttribute('href'));
      });
    });

    initializeRegistrationForms(root);

    const success = root.querySelector('#ClarstonSupportSuccess');
    if (success) {
      window.requestAnimationFrame(() => {
        success.scrollIntoView({
          behavior: reduceMotion.matches ? 'auto' : 'smooth',
          block: 'center'
        });
        success.focus({ preventScroll: true });
      });
    }
  }

  function initializeAll(scope = document) {
    scope.querySelectorAll('[data-clarston-care]').forEach(initializeCustomerCare);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAll(event.target);
  });
})();
